export interface SdkExample {
  title: string;
  code: string;
}

export interface SdkDoc {
  slug: string;
  label: string;
  badge: string;
  summary: string;
  capabilities: string[];
  installation: {
    description: string;
    code: string;
  };
  descriptorSteps: string[];
  examples: SdkExample[];
  bounds: string[];
  availability: string;
}

export const SDK_DOCS: SdkDoc[] = [
  {
    slug: "sdks/rust",
    label: "Rust client",
    badge: "Managed-compatible · native QUIC",
    summary:
      "The published woven-client Rust SDK supports the Woven Host managed flow. This guide uses native QUIC, verified TLS, Bearer authentication, admission, subscription, publish, receive, and shutdown.",

    capabilities: [
      "Verified native QUIC with ClientTlsConfig and normal certificate chain, validity, and hostname/IP SAN checks",
      "Explicit Bearer authentication through Client::connect_with_tls_and_auth",
      "Managed admission with request_admission and the queue status, heartbeat, claim, and cancel operations",
      "Bounded admission scheduling through admit_with_cancellation",
      "Space subscription, server-assigned entities, reliable events, receive deadlines, and bounded graceful shutdown",
      "Message-only logger().info/warn/error and log(), with session/connection metadata in Host Logs",
    ],
    installation: {
      description:
        "Install the published woven-client 0.3.0 SDK from crates.io with cargo add woven-client@0.3.0 woven-protocol@0.3.0; the protocol crate supplies types used below. Source-path development remains supported: the example below uses sibling Woven/application checkouts instead of registry dependencies. Pin a reviewed source revision for that option.",
      code: `[dependencies]
woven-client = { path = "../woven/crates/woven-client-rust" }
woven-protocol = { path = "../woven/crates/woven-protocol" }
rustls = { version = "0.23", default-features = false, features = ["ring", "std"] }
serde_json = "1"
tokio = { version = "1", features = ["macros", "rt-multi-thread", "sync", "time"] }`,
    },
    descriptorSteps: [
      "After recent account reauthentication, request POST /v1/servers/:id/connection with the server's current generation. The authenticated Host response is the connection descriptor; do not put its token in a URL or log it.",
      "The descriptor endpoint is host:port, so construct quic://host:port for ClientConfig.url. The transport value is quic.",
      "Keep namespaceId, sessionId, spaceId, epoch, and channelIds as canonical nonzero decimal u64 strings in JSON. Parse them to u64 in Rust instead of converting them through a JavaScript number.",
      "Build ClientTlsConfig from descriptor caPem when supplied, or from a populated application-owned rustls::RootCertStore for a publicly trusted certificate. Supply roots explicitly and keep remote TLS verification enabled.",
    ],
    examples: [
      {
        title: "Connect from a Host descriptor",
        code: `use woven_client::{Client, ClientConfig, ClientTlsConfig};
use woven_protocol::AuthenticationScheme;

async fn connect(
    descriptor: &serde_json::Value,
    application_roots: Option<rustls::RootCertStore>,
) -> Result<(Client, u64, u64), Box<dyn std::error::Error>> {
    let endpoint = descriptor["endpoint"].as_str().ok_or("missing endpoint")?;
    let token = descriptor["token"].as_str().ok_or("missing token")?;
    let namespace_id = descriptor["namespaceId"]
        .as_str().ok_or("missing namespaceId")?.parse::<u64>()?;
    let session_id = descriptor["sessionId"]
        .as_str().ok_or("missing sessionId")?.parse::<u64>()?;
    let tls = match descriptor["caPem"].as_str() {
        Some(ca_pem) => ClientTlsConfig::from_ca_pem(ca_pem.as_bytes())?,
        None => ClientTlsConfig::with_root_certificates(
            application_roots.ok_or("no TLS roots supplied")?,
        )?,
    };
    let client = Client::connect_with_tls_and_auth(
        ClientConfig {
            url: format!("quic://{endpoint}"),
            token: token.to_owned(),
            ..ClientConfig::default()
        },
        tls,
        AuthenticationScheme::Bearer,
    )
    .await?;

    Ok((client, namespace_id, session_id))
}`,
      },
      {
        title: "Wait for managed admission with a stop path",
        code: `use std::time::Duration;
use woven_client::ManagedAdmissionOutcome;
use woven_protocol::{AdmissionStatus, QueueState};

let (cancel_tx, cancel_rx) = tokio::sync::oneshot::channel::<()>();
// Move cancel_tx to the owner of your explicit stop control and send ().
let cancellation = async move {
    let _ = cancel_rx.await;
};

let (mut client, outcome) = client
    .admit_with_cancellation(
        namespace_id,
        session_id,
        "one-logical-attempt".to_owned(),
        Duration::from_secs(30),
        cancellation,
    )
    .await?;

match outcome {
    ManagedAdmissionOutcome::Admission(result)
        if result.status == AdmissionStatus::Admitted => {}
    ManagedAdmissionOutcome::Queue(update)
        if update.state == QueueState::Admitted => {}
    other => return Err(format!("admission outcome: {other:?}").into()),
}`,
      },
      {
        title: "Send a message to Host Logs",
        code: `// After managed admission returns Admitted; no space subscription is needed.
client.logger().info("Scene loaded").await?;
client.logger().warn("Optional asset unavailable").await?;
client.logger().error("Failed to save progress").await?;
client.log("Info-level alias").await?;

// Sending is not a database acknowledgement. Continue receiving control traffic
// to observe server rejections. Host stores a rolling 200-event debug history.`,
      },
      {
        title: "Subscribe and publish a reliable event on channel 1",
        code: `use std::time::Duration;
use woven_protocol::{ControlPayload, MessagePayload};

let space = descriptor["spaces"].as_array()
    .and_then(|spaces| spaces.first()).ok_or("missing space")?;
let space_id = space["spaceId"].as_str().ok_or("missing spaceId")?.parse::<u64>()?;
let space_epoch = space["epoch"].as_str().ok_or("missing epoch")?.parse::<u64>()?;
let has_channel_one = space["channelIds"].as_array()
    .is_some_and(|ids| ids.iter().any(|id| id.as_str() == Some("1")));
if !has_channel_one {
    return Err("channel 1 is not allowed for this space".into());
}

client
    .subscribe_space(namespace_id, session_id, space_id, space_epoch, 1)
    .await?;

let accepted = client.recv_timeout(Duration::from_secs(2)).await?
    .ok_or("subscription response timed out")?;
if !matches!(accepted.message,
    MessagePayload::Control(ControlPayload::SubscriptionAccepted(_))) {
    return Err("subscription was not accepted".into());
}

let entered = client.recv_timeout(Duration::from_secs(2)).await?
    .ok_or("entity assignment timed out")?;
if !matches!(entered.message,
    MessagePayload::Control(ControlPayload::EntityEntered(_))) {
    return Err("server did not assign an entity".into());
}
let entity_id = entered.entity_id.ok_or("missing assigned entity ID")?;

client
    .publish_event(
        namespace_id,
        session_id,
        space_id,
        space_epoch,
        1, // channel 1: ReliableOrdered / Ephemeral on the managed node
        entity_id,
        1, // sender sequence; increment for each event in this scope
        1, // application-defined payload type ID
        b"hello from Woven Host".to_vec(),
    )
    .await?;

client.close_gracefully(Duration::from_secs(2)).await?;`,
      },
    ],
    bounds: [
      "The complete DNS, TLS, and WVN1 handshake is bounded to 10 seconds. Each low-level admission or queue exchange is also bounded to 10 seconds.",
      "admit_with_cancellation requires a positive timeout of at most 15 minutes, owns the fresh pre-subscription client, sends bounded heartbeats, claims offers, and closes the connection on cancellation, deadline, or I/O failure.",
      "Admission and protocol rejections are returned as data or errors; the helper performs no transport retries. Do not silently retry a partial stream exchange or reuse a connection after cancellation.",
      "Use unique nonzero correlation IDs with the low-level queue API, reuse an idempotency key only for the same logical request on the same connection and scope, and keep publish sequences strictly increasing.",
      "Bound application concurrency, publish rate, payload size, receive waits, and shutdown time. A graceful close is a bounded opportunity to send close frames, not proof that the peer received pending data.",
      "Logging takes a nonempty message of at most 1,024 UTF-8 bytes, with 10 client logs per connection per second. The node attaches timestamp and connection/session scope; never log credentials. Host stores the latest 200 events per managed server, including automatic connections/disconnections, without logging ordinary updates. Collection is eventual and best-effort, not lossless archiving.",
    ],
    availability:
      "Managed native QUIC connectivity is implemented and validated in local Host/Woven end-to-end tests with verified TLS and Firebase emulators. This does not establish deployed-endpoint or browser-client validation. Pro and Dedicated are Preview, with private founder access first, and use this public Rust client contract.",
  },
  {
    slug: "sdks/javascript",
    label: "TypeScript client",
    badge: "Online · browser WebTransport",
    summary:
      "The @signalweave/woven-client TypeScript client is Online for the managed browser path. It carries WVN1 over WebTransport and HTTP/3/QUIC to the same managed Woven runtime used by native QUIC clients; WebTransport is a separate browser-facing endpoint, not the native QUIC socket.",
    capabilities: [
      "WHATWG WebTransport connections through the Host descriptor's webTransport.url",
      "Explicit Bearer authentication through WovenClient.connect",
      "Optional SHA-256 certificate pinning through webTransport.certificateSha256 and serverCertificateHashes",
      "Managed admission with requestAdmission and the queue status, heartbeat, claim, and cancel operations",
      "Bounded admission scheduling through admitWithCancellation and an AbortController stop path",
      "Space subscription, server-assigned entities, reliable events, receive deadlines, and explicit shutdown",
      "Message-only logger.info/warn/error and log(), with session/connection metadata in Host Logs",
    ],
    installation: {
      description:
        "Install the published @signalweave/woven-client 0.3.0 SDK from npm with npm install @signalweave/woven-client@0.3.0. For source-path development instead, use the sibling-checkout commands below and pin a reviewed source revision. The managed browser transport remains WebTransport.",
      code: `npm --prefix ../woven/crates/woven-client-ts ci
npm --prefix ../woven/crates/woven-client-ts run build
npm install ../woven/crates/woven-client-ts`,
    },
    descriptorSteps: [
      "After recent account reauthentication, request POST /v1/servers/:id/connection with the server's current generation. Keep the returned token out of source control, URLs, and logs. It is one shared product-generation possession credential for trusted/internal clients, not end-user identity.",
      "Pass webTransport.url to WovenClient.connect exactly as returned. It is the managed browser endpoint for WVN1 over HTTP/3/QUIC; it reaches the same managed runtime as native QUIC but is not the same socket.",
      "If webTransport.certificateSha256 is present, require exactly 64 lowercase hexadecimal characters, convert each pair to one byte, and pass the resulting 32-byte value as a sha-256 serverCertificateHashes entry. Without a hash, the browser uses its normal Web PKI validation.",
      "Browser JavaScript cannot install an arbitrary caPem trust root. caPem is for native clients; do not convert it into a browser certificate override or disable verification.",
      "Keep namespaceId, sessionId, spaceId, epoch, and channelIds as canonical nonzero decimal u64 strings in JSON, then parse them with BigInt. Never convert protocol scope IDs through Number.",
    ],
    examples: [
      {
        title: "Connect from a Host browser descriptor",
        code: `import {
  AdmissionStatus,
  AuthenticationScheme,
  MessageKind,
  QueueState,
  WovenClient,
} from "@signalweave/woven-client";

type HostConnectionDescriptor = {
  token: string;
  namespaceId: string;
  sessionId: string;
  webTransport: {
    url: string;
    certificateSha256?: string;
  };
  spaces: Array<
    | {
        kind: "logical";
        spaceId: "1" | "2";
        epoch: "1";
        channelIds: ["1", "4"];
      }
    | {
        kind: "spatialGrid3D";
        slug: string;
        displayName: string;
        spaceId: string;
        epoch: "1";
        channelIds: ["1", "4"];
        metersPerUnit: number;
        cellSize: number;
        interestRadius: number;
        exactDistance: boolean;
        bounds: {
          min: { x: number; y: number; z: number };
          max: { x: number; y: number; z: number };
        };
      }
  >;
};

const MAX_U64 = 18_446_744_073_709_551_615n;

function scopeId(value: string, name: string): bigint {
  if (!/^[1-9][0-9]*$/.test(value)) throw new Error(\`invalid \${name}\`);
  const parsed = BigInt(value);
  if (parsed > MAX_U64) throw new Error(\`\${name} exceeds u64\`);
  return parsed;
}

function sha256Bytes(hex: string): Uint8Array {
  if (!/^[0-9a-f]{64}$/.test(hex)) {
    throw new Error("certificateSha256 must be 64 lowercase hex characters");
  }
  return Uint8Array.from(
    { length: 32 },
    (_, index) => Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16),
  );
}

const descriptor: HostConnectionDescriptor = await connectionResponse.json();
const certificateSha256 = descriptor.webTransport.certificateSha256;
const client = await WovenClient.connect({
  url: descriptor.webTransport.url,
  token: descriptor.token,
  authenticationScheme: AuthenticationScheme.Bearer,
  webTransportOptions: certificateSha256
    ? {
        serverCertificateHashes: [
          { algorithm: "sha-256", value: sha256Bytes(certificateSha256) },
        ],
      }
    : undefined,
});

const namespaceId = scopeId(descriptor.namespaceId, "namespaceId");
const sessionId = scopeId(descriptor.sessionId, "sessionId");`,
      },
      {
        title: "Wait for managed admission with a stop path",
        code: `const cancellation = new AbortController();
// Call cancellation.abort() from your application's explicit stop control.
const outcome = await client.admitWithCancellation(
  namespaceId,
  sessionId,
  crypto.randomUUID(),
  60_000,
  cancellation.signal,
);

const admitted =
  outcome.kind === "admission"
    ? outcome.result.status === AdmissionStatus.Admitted
    : outcome.update.state === QueueState.Admitted;
if (!admitted) {
  client.close(1, "not admitted");
  throw new Error("managed admission did not admit this connection");
}`,
      },
      {
        title: "Subscribe to channel 1, receive entity assignment, then publish a reliable event",
        code: `const space = descriptor.spaces[0];
if (!space) throw new Error("descriptor has no spaces");
const channel = "1";
if (!space.channelIds.includes(channel)) {
  throw new Error("channel 1 is not allowed for this space");
}

const spaceId = scopeId(space.spaceId, "spaceId");
const spaceEpoch = scopeId(space.epoch, "spaceEpoch");
const channelId = scopeId(channel, "channelId");

// Managed admission has already returned Admitted before this subscription.
await client.subscribeSpace(namespaceId, sessionId, spaceId, spaceEpoch, channelId);

// Treat SubscriptionRejected as fatal. Publish only after SubscriptionAccepted
// is followed by EntityEntered with the server-assigned entity ID.
const subscriptionReply = await client.recvTimeout(2_000);
if (subscriptionReply?.messageKind !== MessageKind.SubscriptionAccepted) {
  throw new Error("subscription was not accepted");
}
const entityAssignment = await client.recvTimeout(2_000);
if (entityAssignment?.messageKind !== MessageKind.EntityEntered) {
  throw new Error("server did not assign an entity");
}
const entityId = entityAssignment.entityId;
if (entityId === null) throw new Error("entity assignment omitted its entity ID");

await client.publishEvent(
  namespaceId,
  sessionId,
  spaceId,
  spaceEpoch,
  channelId,
  entityId,
  1n, // sender sequence; increment for each event in this scope
  1n, // application-defined payload type ID
  new TextEncoder().encode("hello from Woven Host"),
);

client.close();`,
      },
      {
        title: "Send a message to Host Logs",
        code: `// After managed admission returns Admitted; no space subscription is needed.
await client.logger.info("Scene loaded");
await client.logger.warn("Optional asset unavailable");
await client.logger.error("Failed to save progress");
await client.log("Info-level alias");

// Await sequentially: these share the existing ordered control stream.
// Sending is not database acknowledgement; keep receiving server rejections.`,
      },
    ],
    bounds: [
      "WovenClient.connect bounds WebTransport readiness to 10 seconds by default and completes the WVN1 Hello, Capabilities, Authenticate, and Authenticated handshake with explicit Bearer credentials.",
      "admitWithCancellation requires a positive timeout of at most 15 minutes, uses bounded exchanges and polling, and closes the connection on abort, deadline, transport failure, or protocol mismatch. Create a fresh client before retrying after cancellation.",
      "Admission and queue rejections are returned as semantic data; the helper performs no transport retries. Do not silently replay a partial stream exchange.",
      "Subscribe only after an Admitted outcome. Publish only after SubscriptionAccepted and EntityEntered provide the server-assigned entity ID, and keep sender sequences strictly increasing.",
      "The optional certificateSha256 pin must decode to exactly 32 bytes. Browser code cannot add arbitrary CA PEM trust roots; use the descriptor's browser URL and normal Web PKI when no certificate hash is supplied.",
      "Bound application concurrency, publish rate, payload size, receive waits, and shutdown behavior. Keep all protocol IDs as bigint so u64 values remain exact.",
      "Credential rotation and short-lived browser grants are not implemented. If this shared token may be compromised, delete and recreate the product before reconnecting trusted clients.",
      "After admission, await client.logger.info/warn/error(message) sequentially. Messages are limited to 1,024 UTF-8 bytes and 10 per connection per second; no tokens or sensitive data. Host keeps the latest 200 events per managed server, including connections/disconnections, not noisy state updates. Sending is not database acknowledgement, and collection is eventual and best-effort.",
    ],
    availability:
      "Online for managed browser WebTransport. Use only the endpoint, certificate metadata, logical spaces, and managed spatial definitions returned by Host; persistence beyond Ephemeral and Inference remain Preview.",
  },
];
